import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { saApi } from "../../api/superAdminApi";
import { ROLE_OPTIONS } from "../../constants/roles";
import { Box, Heading, Spinner, Tabs, TabList, TabPanels, Tab, TabPanel, Stack, FormControl, FormLabel, Input, Switch, Button, HStack, Table, Thead, Tr, Th, Tbody, Td, Select, Flex, useDisclosure, Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter, useToast, Tag, TagLabel, TagCloseButton, Icon, Text, SimpleGrid, Spacer, useColorModeValue, Menu, MenuButton, MenuList, MenuOptionGroup, MenuItemOption, Badge, Center } from "@chakra-ui/react";
import { FaCloudUploadAlt } from "react-icons/fa";
import { FiChevronDown } from "react-icons/fi";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import EditBUUserModal from "./components/EditBUUserModal";
import EditCompanyUserModal from "./components/EditCompanyUserModal";
import ConfirmDialog from "../../components/Portal/ConfirmDialog";

const CompanyDetail = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [company, setCompany] = useState(null);
  const [companyBaseline, setCompanyBaseline] = useState(null);
  const [users, setUsers] = useState([]);
  const [invite, setInvite] = useState({ email: "", first_name: "", last_name: "", role: "Manager", business_unit_id: "" });
  const { isOpen: isInviteOpen, onOpen: openInvite, onClose: closeInvite } = useDisclosure();
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const toast = useToast();
  const { isOpen: isCreateOpen, onOpen: openCreate, onClose: closeCreate } = useDisclosure();
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const bgHover = useColorModeValue("gray.50", "gray.700");

  // Templates state (scoped to company)
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesError, setTemplatesError] = useState("");
  const [templates, setTemplates] = useState([]);

  // Create template form state
  const [file, setFile] = useState(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [docType] = useState("CV");
  const [uploadBuId, setUploadBuId] = useState("");
  const [createSubmitting, setCreateSubmitting] = useState(false);


  // Soft-delete confirmation state (company + per-environment)
  const { isOpen: isDeleteCompanyOpen, onOpen: openDeleteCompany, onClose: closeDeleteCompany } = useDisclosure();
  const [deletingCompany, setDeletingCompany] = useState(false);
  const { isOpen: isDeleteBUOpen, onOpen: openDeleteBU, onClose: closeDeleteBU } = useDisclosure();
  const [buDeleteTarget, setBuDeleteTarget] = useState(null);
  const [deletingBU, setDeletingBU] = useState(false);

  // Business units (admin scope)
  const [units, setUnits] = useState([]);
  const [selectedUnitId, setSelectedUnitId] = useState("");
  const { isOpen: isBUCreateOpen, onOpen: openBUCreate, onClose: closeBUCreate } = useDisclosure();
  const { isOpen: isBUEditOpen, onOpen: openBUEdit, onClose: closeBUEdit } = useDisclosure();
  const [newBU, setNewBU] = useState({ unit_name: "", manager_name: "", billing_manager_name: "", billing_manager_email: "", has_profile: true, has_cv: true, has_skills: true, has_pilot: true, has_partners: true });
  const [editBU, setEditBU] = useState({ id: "", unit_name: "", manager_name: "", billing_manager_name: "", billing_manager_email: "", has_profile: true, has_cv: true, has_skills: true, has_pilot: true, has_partners: true });
  const [buSaving, setBuSaving] = useState(false);

  // Users in selected BU (admin scope)
  const [buUsers, setBuUsers] = useState([]);
  const [loadingBuUsers, setLoadingBuUsers] = useState(false);
  const { isOpen: isEditUserOpen, onOpen: openEditUser, onClose: closeEditUser } = useDisclosure();
  const [editingUser, setEditingUser] = useState(null);
  const [membershipsByUserId, setMembershipsByUserId] = useState({});
  const [membershipsLoading, setMembershipsLoading] = useState(false);

  // Company users (not assigned to any BU)
  const [companyUsers, setCompanyUsers] = useState([]);
  const [loadingCompanyUsers, setLoadingCompanyUsers] = useState(false);
  const [pilotLicenseSummary, setPilotLicenseSummary] = useState({ limit: 0, assigned: 0, available: 0 });
  const [licensedPilotUserIds, setLicensedPilotUserIds] = useState([]);
  const [pilotLicenseUpdatingUserId, setPilotLicenseUpdatingUserId] = useState(null);
  const { isOpen: isInviteCompanyUserOpen, onOpen: openInviteCompanyUser, onClose: closeInviteCompanyUser } = useDisclosure();
  const { isOpen: isEditCompanyUserOpen, onOpen: openEditCompanyUser, onClose: closeEditCompanyUser } = useDisclosure();
  const [editingCompanyUser, setEditingCompanyUser] = useState(null);
  const [companyInvite, setCompanyInvite] = useState({ email: "", first_name: "", last_name: "", role: "Company Admin" });
  const [companyInviteSubmitting, setCompanyInviteSubmitting] = useState(false);

  // Local string state for pilot license limit input so the field can be cleared and typed freely
  const [pilotLicenseLimitInput, setPilotLicenseLimitInput] = useState("");

  // ASAP PROFILE / PDL safeguards (per-company)
  const [pdlLimits, setPdlLimits] = useState(null);
  const [pdlLimitsLoading, setPdlLimitsLoading] = useState(false);
  const [pdlSearchBudgetInput, setPdlSearchBudgetInput] = useState("");
  const [pdlMaxLoadMoreInput, setPdlMaxLoadMoreInput] = useState("");
  const [pdlSaving, setPdlSaving] = useState(false);

  const loadPdlLimits = useCallback(async () => {
    setPdlLimitsLoading(true);
    try {
      const data = await saApi.getCompanyPdlLimits(companyId);
      setPdlLimits(data);
      setPdlSearchBudgetInput(String(data?.monthlySearchBudget ?? 0));
      setPdlMaxLoadMoreInput(String(data?.maxLoadMorePerSearch ?? 0));
    } catch (e) {
      // Silent: if backend is older or company has no row yet, the
      // component renders defaults and the save call will create the row.
    } finally {
      setPdlLimitsLoading(false);
    }
  }, [companyId]);

  const savePdlLimits = async () => {
    const budget = Math.max(0, parseInt(pdlSearchBudgetInput, 10) || 0);
    const maxLoadMore = Math.max(0, parseInt(pdlMaxLoadMoreInput, 10) || 0);
    setPdlSaving(true);
    try {
      const data = await saApi.updateCompanyPdlLimits(companyId, {
        monthlySearchBudget: budget,
        maxLoadMorePerSearch: maxLoadMore,
      });
      setPdlLimits(data);
      setPdlSearchBudgetInput(String(data.monthlySearchBudget));
      setPdlMaxLoadMoreInput(String(data.maxLoadMorePerSearch));
      toast({ title: "Profile search safeguards updated", status: "success" });
    } catch (e) {
      const message = e?.response?.data?.detail || e?.message || "Save failed";
      toast({ title: "Could not save profile search safeguards", description: message, status: "error" });
    } finally {
      setPdlSaving(false);
    }
  };

  const extractPilotLicensedUserIds = (payload) => {
    if (!payload) return [];
    const users = Array.isArray(payload?.assigned_users)
      ? payload.assigned_users
      : Array.isArray(payload?.users)
        ? payload.users
        : Array.isArray(payload?.items)
          ? payload.items
          : [];
    return users
      .map((u) => u?.id ?? u?.user_id)
      .filter((id) => id !== null && id !== undefined)
      .map((id) => String(id));
  };

  const loadPilotLicenseState = async ({ fallbackCompany } = {}) => {
    try {
      const data = await saApi.getCompanyPilotLicenses(companyId);
      const ids = extractPilotLicensedUserIds(data);
      setLicensedPilotUserIds(ids);
      setPilotLicenseSummary({
        limit: Number(data?.limit ?? data?.pilot_license_limit ?? fallbackCompany?.pilot_license_limit ?? 0),
        assigned: Number(data?.assigned ?? data?.pilot_license_assigned ?? ids.length ?? 0),
        available: Number(
          data?.available ??
          data?.pilot_license_available ??
          Math.max(
            Number(data?.limit ?? data?.pilot_license_limit ?? fallbackCompany?.pilot_license_limit ?? 0) -
            Number(data?.assigned ?? data?.pilot_license_assigned ?? ids.length ?? 0),
            0
          )
        ),
      });
    } catch (e) {
      const fallbackLimit = Number(fallbackCompany?.pilot_license_limit ?? 0);
      const fallbackAssigned = Number(fallbackCompany?.pilot_license_assigned ?? 0);
      const fallbackAvailable = Number(fallbackCompany?.pilot_license_available ?? Math.max(fallbackLimit - fallbackAssigned, 0));
      setPilotLicenseSummary({ limit: fallbackLimit, assigned: fallbackAssigned, available: fallbackAvailable });
      setLicensedPilotUserIds([]);
    }
  };

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [c, uList, buList] = await Promise.all([
        saApi.getCompany(companyId),
        saApi.listCompanyUsers(companyId),
        saApi.listCompanyBusinessUnits(companyId)
      ]);
      setCompany(c);
      setCompanyBaseline(JSON.parse(JSON.stringify(c)));
      setUsers(Array.isArray(uList) ? uList : []);
      const safeUnits = Array.isArray(buList) ? buList : [];
      setUnits(safeUnits);
      if (!selectedUnitId && safeUnits.length) setSelectedUnitId(String(safeUnits[0].id));
      // Load company users (not assigned to any BU)
      await loadCompanyUsers();
      await loadPilotLicenseState({ fallbackCompany: c });
    } catch (e) {
      setError("Failed to load company");
    } finally {
      setLoading(false);
    }
  };

  const loadCompanyUsers = async () => {
    setLoadingCompanyUsers(true);
    try {
      const list = await saApi.listCompanyUsers(companyId, { business_unit_id: null });
      setCompanyUsers(Array.isArray(list) ? list : []);
    } catch (e) {
      setCompanyUsers([]);
    } finally {
      setLoadingCompanyUsers(false);
    }
  };

  const loadTemplates = async () => {
    setTemplatesLoading(true);
    setTemplatesError("");
    try {
      const data = await saApi.listTemplates({ company_id: companyId });
      setTemplates(Array.isArray(data) ? data : []);
    } catch (e) {
      setTemplatesError("Failed to load templates");
    } finally {
      setTemplatesLoading(false);
    }
  };

  useEffect(() => { load(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  useEffect(() => { loadTemplates(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  useEffect(() => { loadPdlLimits(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  // Sync pilot license limit input from company when data is loaded or company changes
  useEffect(() => {
    if (company == null) return;
    const limit = company.pilot_license_limit;
    const str = limit === "" || limit === null || limit === undefined ? "" : String(Number(limit));
    setPilotLicenseLimitInput(str);
  }, [company?.id, company?.pilot_license_limit]);

  const saveCompany = async () => {
    const trimmed = (pilotLicenseLimitInput || "").trim();
    const limit = trimmed === "" ? 0 : Math.max(0, parseInt(trimmed, 10) || 0);
    const companyToSave = { ...company, pilot_license_limit: limit };
    try {
      await saApi.updateCompany(companyId, companyToSave);
      setPilotLicenseLimitInput(trimmed === "" ? "0" : String(limit));
      await load();
      toast({ title: "Company updated", status: "success" });
    } catch (e) {
      const status = e?.response?.status;
      const message = e?.response?.data?.message || e?.response?.data?.detail || e?.message;
      if (status === 409) {
        toast({
          title: "Cannot update license count",
          description: message || "The new license count is less than the number of assigned licenses. Unassign licenses from users first, then save.",
          status: "warning",
          duration: 8000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Failed to save company",
          description: message || "Please try again.",
          status: "error",
          isClosable: true,
        });
      }
    }
  };

  const handleDeleteCompany = async () => {
    setDeletingCompany(true);
    try {
      await saApi.deleteCompany(companyId);
      toast({ title: t("superAdmin.deleteCompany.success"), status: "success" });
      // Navigate on the next tick so the dialog finishes closing before the
      // page unmounts, avoiding a React removeChild crash during teardown.
      setTimeout(() => navigate("/admin/configuration"), 0);
    } catch (err) {
      toast({ title: t("superAdmin.deleteCompany.error"), status: "error" });
    } finally {
      setDeletingCompany(false);
    }
  };

  const askDeleteBU = (e, unit) => {
    e.stopPropagation();
    setBuDeleteTarget(unit);
    openDeleteBU();
  };

  const handleDeleteBU = async () => {
    if (!buDeleteTarget) return;
    const wasSelected = String(selectedUnitId) === String(buDeleteTarget.id);
    setDeletingBU(true);
    try {
      await saApi.deleteCompanyBusinessUnit(companyId, buDeleteTarget.id);
      toast({ title: t("superAdmin.deleteBusinessUnit.success"), status: "success" });
      const refreshed = await saApi.listCompanyBusinessUnits(companyId);
      // Apply the list change on the next tick so the dialog finishes closing
      // (and returns focus to its trigger row) before that row is removed,
      // avoiding a React removeChild crash.
      setTimeout(() => {
        setUnits(Array.isArray(refreshed) ? refreshed : []);
        if (wasSelected) {
          setSelectedUnitId("");
          setBuUsers([]);
        }
        setBuDeleteTarget(null);
      }, 0);
    } catch (err) {
      const message = err?.response?.data?.message || err?.message;
      toast({ title: t("superAdmin.deleteBusinessUnit.error"), description: message, status: "error" });
    } finally {
      setDeletingBU(false);
    }
  };

  const resetUploadForm = () => {
    setFile(null);
    setName("");
    setDescription("");
    setUploadBuId("");
  };

  const createTemplate = async () => {
    if (!file || !name || !uploadBuId) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("company_id", String(companyId));
    fd.append("name", name);
    if (description) fd.append("description", description);
    if (docType) fd.append("doc_type", docType);
    fd.append("business_unit_id", String(uploadBuId));
    await saApi.uploadTemplate(fd);
    resetUploadForm();
    await loadTemplates();
  };

  const removeTemplate = async (id) => {
    await saApi.deleteTemplate(id);
    await loadTemplates();
  };
  
  // Load business unit users when selection changes
  useEffect(() => {
    const fetchBuUsers = async () => {
      if (!selectedUnitId) { setBuUsers([]); return; }
      setLoadingBuUsers(true);
      try {
        const list = await saApi.listCompanyUsers(companyId, { business_unit_id: selectedUnitId });
        setBuUsers(Array.isArray(list) ? list : []);
      } catch (e) {
        setBuUsers([]);
      } finally {
        setLoadingBuUsers(false);
      }
    };
    fetchBuUsers();
  }, [companyId, selectedUnitId]);

  const createBU = async () => {
    setBuSaving(true);
    try {
      await saApi.createCompanyBusinessUnit(companyId, newBU);
      toast({ title: "Business unit created", status: "success" });
      setNewBU({ unit_name: "", manager_name: "", billing_manager_name: "", billing_manager_email: "", has_profile: true, has_cv: true, has_skills: true, has_pilot: true, has_partners: true });
      closeBUCreate();
      const refreshed = await saApi.listCompanyBusinessUnits(companyId);
      setUnits(Array.isArray(refreshed) ? refreshed : []);
      if (!selectedUnitId && Array.isArray(refreshed) && refreshed.length) setSelectedUnitId(String(refreshed[0].id));
    } catch (e) {
      toast({ title: "Failed to create BU", status: "error" });
    } finally {
      setBuSaving(false);
    }
  };

  const saveBU = async () => {
    if (!editBU?.id) return;
    setBuSaving(true);
    try {
      await saApi.updateCompanyBusinessUnit(companyId, editBU.id, {
        unit_name: editBU.unit_name,
        manager_name: editBU.manager_name,
        billing_manager_name: editBU.billing_manager_name,
        billing_manager_email: editBU.billing_manager_email,
        has_profile: Boolean(editBU.has_profile),
        has_cv: Boolean(editBU.has_cv),
        has_skills: Boolean(editBU.has_skills),
        has_pilot: Boolean(editBU.has_pilot),
        has_partners: Boolean(editBU.has_partners),
      });
      toast({ title: "Business unit updated", status: "success" });
      closeBUEdit();
      const refreshed = await saApi.listCompanyBusinessUnits(companyId);
      setUnits(Array.isArray(refreshed) ? refreshed : []);
    } catch (e) {
      toast({ title: "Failed to update BU", status: "error" });
    } finally {
      setBuSaving(false);
    }
  };

  const inviteToSelectedBU = async () => {
    try {
      const payload = { ...invite };
      payload.business_unit_id = selectedUnitId || "";
      if (!payload.business_unit_id) delete payload.business_unit_id;
      await saApi.inviteCompanyUser(companyId, payload);
      toast({ title: "Invitation sent", status: "success" });
      setInvite({ email: "", first_name: "", last_name: "", role: "Manager", business_unit_id: "" });
      closeInvite();
      // refresh BU user list
      const list = await saApi.listCompanyUsers(companyId, { business_unit_id: selectedUnitId });
      setBuUsers(Array.isArray(list) ? list : []);
    } catch (e) {
      const message = e?.response?.data?.message || e?.message || "Invite failed";
      toast({ title: "Invite failed", description: message, status: "error" });
    } finally {
      setInviteSubmitting(false);
    }
  };



  const buildMembershipsMap = async () => {
    if (!Array.isArray(units) || units.length === 0) {
      return {};
    }
    if (membershipsLoading) {
      return membershipsByUserId || {};
    }
    setMembershipsLoading(true);
    try {
      const results = await Promise.all(
        units.map((bu) => saApi.listCompanyUsers(companyId, { business_unit_id: bu.id }))
      );
      // Map: { userId: { buId: role, ... } }
      const map = {};
      results.forEach((list, idx) => {
        const buId = String(units[idx].id);
        (Array.isArray(list) ? list : []).forEach((user) => {
          const userId = String(user.id);
          if (!map[userId]) {
            map[userId] = {};
          }
          if (!map[userId][buId]) {
            map[userId][buId] = user.bu_role || user.role || "Sales Representative";
          }
        });
      });
      setMembershipsByUserId(map);
      return map;
    } catch (e) {
      toast({ title: "Failed to load user business units", status: "error" });
      return membershipsByUserId || {};
    } finally {
      setMembershipsLoading(false);
    }
  };

  const inviteCompanyUser = async () => {
    try {
      await saApi.inviteCompanyUser(companyId, companyInvite);
      toast({ title: "Invitation sent", status: "success" });
      setCompanyInvite({ email: "", first_name: "", last_name: "", role: "Company Admin" });
      closeInviteCompanyUser();
      await loadCompanyUsers();
    } catch (e) {
      const message = e?.response?.data?.message || e?.message || "Invite failed";
      toast({ title: "Invite failed", description: message, status: "error" });
    } finally {
      setCompanyInviteSubmitting(false);
    }
  };



  const licensedPilotUserIdSet = new Set((licensedPilotUserIds || []).map((id) => String(id)));
  const isPilotLicensedForUser = (u) => {
    const userId = String(u?.id ?? "");
    if (!userId) return false;
    if (licensedPilotUserIdSet.has(userId)) return true;
    return Boolean(u?.pilot_license_enabled ?? u?.has_pilot_license_for_user ?? u?.has_pilot_license);
  };

  const togglePilotLicenseForUser = async (u, shouldAssign) => {
    if (!u?.id || pilotLicenseUpdatingUserId) return;
    setPilotLicenseUpdatingUserId(String(u.id));
    try {
      if (shouldAssign) {
        await saApi.assignCompanyPilotLicense(companyId, u.id);
      } else {
        await saApi.unassignCompanyPilotLicense(companyId, u.id);
      }
      await Promise.all([
        loadPilotLicenseState({ fallbackCompany: company }),
        loadCompanyUsers(),
      ]);
      if (selectedUnitId) {
        const list = await saApi.listCompanyUsers(companyId, { business_unit_id: selectedUnitId });
        setBuUsers(Array.isArray(list) ? list : []);
      }
      toast({
        title: shouldAssign ? "PILOT license assigned" : "PILOT license removed",
        status: "success"
      });
    } catch (e) {
      const message = e?.response?.data?.message || e?.message || "License update failed";
      toast({ title: "License update failed", description: message, status: "error" });
    } finally {
      setPilotLicenseUpdatingUserId(null);
    }
  };



  if (loading) return <Spinner />;
  if (error) return <Box color="red.500">{error}</Box>;
  if (!company) return null;

  // Support both snake_case and camelCase fields from the API
  const businessUnits = Array.isArray(company?.business_units)
    ? company.business_units
    : (Array.isArray(company?.businessUnits) ? company.businessUnits : []);

  const normalizeForCompare = (c) => {
    if (!c) return null;
    return {
      company_name: c.company_name || "",
      is_active: Boolean(c.is_active),
      trial_start_at: c.trial_start_at ? new Date(c.trial_start_at).toISOString() : "",
      trial_end_at: c.trial_end_at ? new Date(c.trial_end_at).toISOString() : "",
      pilot_license_limit: Number(c.pilot_license_limit ?? 0),
    };
  };

  const baseline = normalizeForCompare(companyBaseline);
  const effectiveLicenseLimit = (() => {
    const trimmed = (pilotLicenseLimitInput || "").trim();
    if (trimmed === "") return 0;
    const n = parseInt(pilotLicenseLimitInput, 10);
    return Number.isNaN(n) ? (company?.pilot_license_limit ?? 0) : Math.max(0, n);
  })();
  const current = company ? { ...normalizeForCompare(company), pilot_license_limit: effectiveLicenseLimit } : null;
  const isDirty = baseline && current ? JSON.stringify(baseline) !== JSON.stringify(current) : false;
  const pilotLicenseLimit = Number(company?.pilot_license_limit ?? pilotLicenseSummary?.limit ?? 0);
  const pilotLicenseAssigned = Number(company?.pilot_license_assigned ?? pilotLicenseSummary?.assigned ?? licensedPilotUserIds.length ?? 0);
  const pilotLicenseAvailable = Number(company?.pilot_license_available ?? pilotLicenseSummary?.available ?? Math.max(pilotLicenseLimit - pilotLicenseAssigned, 0));

  return (
    <Box>
      <Heading size="md" mb={4}>{company.company_name} ({company.company_code})</Heading>
      
      <Tabs variant="enclosed" colorScheme="gray">
        <TabList>
          <Tab>Overview</Tab>
          <Tab>Business units & users</Tab>
          <Tab>Template management</Tab>
          <Tab>License history</Tab>
        </TabList>
        <TabPanels>
          <TabPanel>
            <Stack spacing={4} maxW="lg">
              <FormControl>
                <FormLabel>Company name</FormLabel>
                <Input value={company.company_name || ""} onChange={(e) => setCompany({ ...company, company_name: e.target.value })} />
              </FormControl>
              <FormControl display="flex" alignItems="center">
                <FormLabel mb={0}>Active</FormLabel>
                <Switch isChecked={Boolean(company.is_active)} onChange={(e) => setCompany({ ...company, is_active: e.target.checked })} />
              </FormControl>
              <FormControl>
                <FormLabel>Trial start</FormLabel>
                <Input type="datetime-local" value={company.trial_start_at ? new Date(company.trial_start_at).toISOString().slice(0,16) : ""} onChange={(e) => setCompany({ ...company, trial_start_at: new Date(e.target.value).toISOString() })} />
              </FormControl>
              <FormControl>
                <FormLabel>Trial end</FormLabel>
                <Input type="datetime-local" value={company.trial_end_at ? new Date(company.trial_end_at).toISOString().slice(0,16) : ""} onChange={(e) => setCompany({ ...company, trial_end_at: new Date(e.target.value).toISOString() })} />
              </FormControl>
              <FormControl>
                <FormLabel>ASAP PILOT purchased licenses</FormLabel>
                <Input
                  type="number"
                  min={0}
                  step={1}
                  placeholder="0"
                  value={pilotLicenseLimitInput}
                  onChange={(e) => setPilotLicenseLimitInput(e.target.value)}
                  onBlur={() => {
                    const trimmed = pilotLicenseLimitInput.trim();
                    if (trimmed === "") {
                      setCompany((prev) => ({ ...prev, pilot_license_limit: 0 }));
                      setPilotLicenseLimitInput("0");
                    } else {
                      const n = parseInt(pilotLicenseLimitInput, 10);
                      if (!Number.isNaN(n) && n >= 0) {
                        setCompany((prev) => ({ ...prev, pilot_license_limit: n }));
                        setPilotLicenseLimitInput(String(n));
                      }
                    }
                  }}
                />
              </FormControl>
              <HStack spacing={6}>
                <Text fontSize="sm">Assigned: <Text as="span" fontWeight="bold">{pilotLicenseAssigned}</Text></Text>
                <Text fontSize="sm">Available: <Text as="span" fontWeight="bold">{pilotLicenseAvailable}</Text></Text>
              </HStack>
              <HStack>
                <Button colorScheme="teal" onClick={saveCompany} isDisabled={!isDirty}>Save</Button>
              </HStack>

              <Box pt={6} mt={2} borderTopWidth="1px" borderColor={borderColor}>
                <Heading as="h3" size="sm" mb={1} sx={{ marginBottom: "0 !important" }}>
                  ASAP PROFILE: profile search safeguards
                </Heading>
                <Text fontSize="xs" color="gray.500" mt={1} mb={3}>
                  Each search is billable. The monthly budget caps how many searches the
                  company can create in the current calendar month; the per-search cap
                  limits how many additional "Load more" calls a single search can trigger.
                </Text>
                <Stack spacing={4}>
                  <FormControl>
                    <FormLabel>Monthly search budget (0 = unlimited)</FormLabel>
                    <Input
                      type="number"
                      min={0}
                      step={1}
                      placeholder="0"
                      value={pdlSearchBudgetInput}
                      onChange={(e) => setPdlSearchBudgetInput(e.target.value)}
                      data-testid="pdl-search-budget-input"
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Max additional load-more calls per search</FormLabel>
                    <Input
                      type="number"
                      min={0}
                      step={1}
                      placeholder="4"
                      value={pdlMaxLoadMoreInput}
                      onChange={(e) => setPdlMaxLoadMoreInput(e.target.value)}
                      data-testid="pdl-max-load-more-input"
                    />
                  </FormControl>
                  <HStack spacing={6}>
                    <Text fontSize="sm">
                      This month ({pdlLimits?.currentMonth || "—"}):{" "}
                      <Text as="span" fontWeight="bold" data-testid="pdl-searches-used">
                        {pdlLimits?.currentMonthSearchesUsed ?? 0}
                      </Text>{" "}
                      searches used
                    </Text>
                    {pdlLimits?.currentMonthSearchesRemaining != null && (
                      <Text fontSize="sm">
                        Remaining:{" "}
                        <Text as="span" fontWeight="bold" data-testid="pdl-searches-remaining">
                          {pdlLimits.currentMonthSearchesRemaining}
                        </Text>
                      </Text>
                    )}
                  </HStack>
                  <HStack>
                    <Button
                      colorScheme="teal"
                      onClick={savePdlLimits}
                      isLoading={pdlSaving}
                      isDisabled={pdlLimitsLoading}
                      data-testid="pdl-limits-save"
                    >
                      Save profile search safeguards
                    </Button>
                  </HStack>
                </Stack>
              </Box>

              <Box pt={6} mt={2} borderTopWidth="1px" borderColor="red.200">
                <Heading as="h3" size="sm" mb={1} color="red.600" sx={{ marginBottom: "0 !important" }}>
                  {t("superAdmin.deleteCompany.title")}
                </Heading>
                <Text fontSize="xs" color="gray.500" mt={1} mb={3}>
                  {t("superAdmin.deleteCompany.body", { name: company?.company_name || company?.company_code || "" })}
                </Text>
                <Button colorScheme="red" onClick={openDeleteCompany}>
                  {t("superAdmin.deleteCompany.confirm")}
                </Button>
              </Box>
            </Stack>
          </TabPanel>
          <TabPanel>
            {/* Company Users section */}
            <Flex justify="space-between" align="center" mb={3}>
              <Heading as="h3" size="sm">Company Users (Not assigned to any BU)</Heading>
              <Button size="sm" colorScheme="blue" onClick={openInviteCompanyUser}>Invite Company User</Button>
            </Flex>

            {/* Company Users table */}
            <Table variant="simple" size="sm" mb={6} bg="white" borderWidth="1px" borderRadius="md">
              <Thead>
                <Tr>
                  <Th>Name</Th>
                  <Th>Email</Th>
                  <Th>Role</Th>
                  <Th>PILOT license</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {loadingCompanyUsers ? (
                  <Tr><Td colSpan={5}><Spinner size="sm" /></Td></Tr>
                ) : (companyUsers && companyUsers.length > 0) ? (
                  companyUsers.map((u) => (
                    <Tr key={u.id}>
                      <Td>{[u.first_name, u.last_name].filter(Boolean).join(" ") || "-"}</Td>
                      <Td>{u.email}</Td>
                      <Td>{u.role === "COMPANY_ADMIN" || u.role === "Company Admin" ? "Company Admin" : u.role || "-"}</Td>
                      <Td>
                        <Tag size="sm" variant="subtle" colorScheme={isPilotLicensedForUser(u) ? "green" : "red"}>
                          <TagLabel>{isPilotLicensedForUser(u) ? "Assigned" : "Not assigned"}</TagLabel>
                        </Tag>
                      </Td>
                      <Td>
                        <HStack spacing={2}>
                          <Button
                            size="xs"
                            colorScheme={isPilotLicensedForUser(u) ? "red" : "blue"}
                            variant="outline"
                            isLoading={pilotLicenseUpdatingUserId === String(u.id)}
                            onClick={() => togglePilotLicenseForUser(u, !isPilotLicensedForUser(u))}
                          >
                            {isPilotLicensedForUser(u) ? "Remove license" : "Assign license"}
                          </Button>
                          <Button size="xs" variant="outline" onClick={() => { setEditingCompanyUser({ ...u }); openEditCompanyUser(); }}>Edit</Button>
                        </HStack>
                      </Td>
                    </Tr>
                  ))
                ) : (
                  <Tr>
                    <Td colSpan={5} textAlign="center" py={6}>No company users found. These are users who are part of the company but not assigned to any business unit.</Td>
                  </Tr>
                )}
              </Tbody>
            </Table>

            {/* Business Units header */}
            <Flex mb={4} justify="space-between" align="center">
              <Heading as="h3" size="sm">Business Units</Heading>
              <Button size="sm" colorScheme="teal" onClick={openBUCreate}>New Business Unit</Button>
            </Flex>

            {/* Business Units table */}
            <Table variant="simple" size="sm" mb={6} bg="white" borderWidth="1px" borderRadius="md">
              <Thead>
                <Tr>
                  <Th>Name</Th>
                  <Th>Code</Th>
                  <Th>Profile</Th>
                  <Th>CV</Th>
                  <Th>Skills</Th>
                  <Th>Pilot</Th>
                  <Th>Partners</Th>
                  <Th>Manager</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {(!units || units.length === 0) ? (
                  <Tr>
                    <Td colSpan={9} textAlign="center" py={6}>No business units found.</Td>
                  </Tr>
                ) : (
                  units.map((u) => (
                    <Tr
                      key={u.id}
                      bg={String(u.id) === String(selectedUnitId) ? "gray.50" : "transparent"}
                      _hover={{ bg: "gray.100" }}
                      cursor="pointer"
                      onClick={() => setSelectedUnitId(String(u.id))}
                    >
                      <Td>{u.unit_name || "-"}</Td>
                      <Td>{u.unit_code || "-"}</Td>
                      <Td><Tag size="sm" variant="subtle" colorScheme={(u.has_profile ?? true) ? "green" : "red"}><TagLabel>{(u.has_profile ?? true) ? "On" : "Off"}</TagLabel></Tag></Td>
                      <Td><Tag size="sm" variant="subtle" colorScheme={(u.has_cv ?? true) ? "green" : "red"}><TagLabel>{(u.has_cv ?? true) ? "On" : "Off"}</TagLabel></Tag></Td>
                      <Td><Tag size="sm" variant="subtle" colorScheme={(u.has_skills ?? true) ? "green" : "red"}><TagLabel>{(u.has_skills ?? true) ? "On" : "Off"}</TagLabel></Tag></Td>
                      <Td><Tag size="sm" variant="subtle" colorScheme={(u.has_pilot ?? true) ? "green" : "red"}><TagLabel>{(u.has_pilot ?? true) ? "On" : "Off"}</TagLabel></Tag></Td>
                      <Td><Tag size="sm" variant="subtle" colorScheme={(u.has_partners ?? true) ? "green" : "red"}><TagLabel>{(u.has_partners ?? true) ? "On" : "Off"}</TagLabel></Tag></Td>
                      <Td>{u.manager_name || "-"}</Td>
                      <Td>
                        <HStack spacing={2}>
                          <Button
                            size="xs"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedUnitId(String(u.id));
                              setEditBU({ id: u.id, unit_name: u.unit_name || "", manager_name: u.manager_name || "", billing_manager_name: u.billing_manager_name || "", billing_manager_email: u.billing_manager_email || "", has_profile: (u.has_profile ?? true), has_cv: (u.has_cv ?? true), has_skills: (u.has_skills ?? true), has_pilot: (u.has_pilot ?? true), has_partners: (u.has_partners ?? true) });
                              openBUEdit();
                            }}
                          >
                            Edit
                          </Button>
                          <Button
                            size="xs"
                            colorScheme="red"
                            variant="outline"
                            onClick={(e) => askDeleteBU(e, u)}
                          >
                            Delete
                          </Button>
                        </HStack>
                      </Td>
                    </Tr>
                  ))
                )}
              </Tbody>
            </Table>

            {/* BU Users header */}
            <Flex justify="space-between" align="center" mb={3}>
              <Heading as="h3" size="sm">Business Unit Users</Heading>
              <Button size="sm" colorScheme="teal" onClick={openInvite} isDisabled={!selectedUnitId}>Invite User to BU</Button>
            </Flex>

            {/* Users table */}
            <Table variant="simple" size="sm" bg="white" borderWidth="1px" borderRadius="md">
              <Thead>
                <Tr>
                  <Th>Name</Th>
                  <Th>Email</Th>
                  <Th>Role</Th>
                  <Th>PILOT license</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {loadingBuUsers ? (
                  <Tr><Td colSpan={5}><Spinner size="sm" /></Td></Tr>
                ) : (buUsers && buUsers.length > 0) ? (
                  buUsers.map((u) => (
                    <Tr key={u.id}>
                      <Td>{[u.first_name, u.last_name].filter(Boolean).join(" ") || "-"}</Td>
                      <Td>{u.email}</Td>
                      <Td>{u.bu_role || u.role || "-"}</Td>
                      <Td>
                        <Tag size="sm" variant="subtle" colorScheme={isPilotLicensedForUser(u) ? "green" : "red"}>
                          <TagLabel>{isPilotLicensedForUser(u) ? "Assigned" : "Not assigned"}</TagLabel>
                        </Tag>
                      </Td>
                      <Td>
                        <HStack spacing={2}>
                          <Button
                            size="xs"
                            colorScheme={isPilotLicensedForUser(u) ? "red" : "blue"}
                            variant="outline"
                            isLoading={pilotLicenseUpdatingUserId === String(u.id)}
                            onClick={() => togglePilotLicenseForUser(u, !isPilotLicensedForUser(u))}
                          >
                            {isPilotLicensedForUser(u) ? "Remove license" : "Assign license"}
                          </Button>
                          <Button
                            size="xs"
                            variant="outline"
                            onClick={async () => {
                              if (Object.keys(membershipsByUserId || {}).length === 0) {
                                await buildMembershipsMap();
                              }
                              setEditingUser({ ...u, role: u.bu_role || u.role });
                              openEditUser();
                            }}
                          >
                            Edit
                          </Button>
                        </HStack>
                      </Td>
                    </Tr>
                  ))
                ) : (
                  <Tr>
                    <Td colSpan={5} textAlign="center" py={6}>This business unit has no users yet. Please invite users to get started.</Td>
                  </Tr>
                )}
              </Tbody>
            </Table>

            {/* Create BU modal */}
            <Modal isOpen={isBUCreateOpen} onClose={closeBUCreate} size="lg">
              <ModalOverlay />
              <ModalContent>
                <ModalHeader>Create Business Unit</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                  <Stack spacing={4}>
                    <FormControl isRequired>
                      <FormLabel>Unit name</FormLabel>
                      <Input value={newBU.unit_name} onChange={(e) => setNewBU({ ...newBU, unit_name: e.target.value })} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Manager's name</FormLabel>
                      <Input value={newBU.manager_name} onChange={(e) => setNewBU({ ...newBU, manager_name: e.target.value })} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Billing manager's name</FormLabel>
                      <Input value={newBU.billing_manager_name} onChange={(e) => setNewBU({ ...newBU, billing_manager_name: e.target.value })} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Billing manager's email</FormLabel>
                      <Input type="email" value={newBU.billing_manager_email} onChange={(e) => setNewBU({ ...newBU, billing_manager_email: e.target.value })} />
                    </FormControl>
                    <Box pt={2}>
                      <FormLabel m={0}>Module access</FormLabel>
                      <Stack spacing={2} mt={2}>
                        <FormControl display="flex" alignItems="center">
                          <FormLabel mb={0} minW="160px">Profile</FormLabel>
                          <Switch isChecked={Boolean(newBU.has_profile)} onChange={(e) => setNewBU({ ...newBU, has_profile: e.target.checked })} />
                        </FormControl>
                        <FormControl display="flex" alignItems="center">
                          <FormLabel mb={0} minW="160px">CV</FormLabel>
                          <Switch isChecked={Boolean(newBU.has_cv)} onChange={(e) => setNewBU({ ...newBU, has_cv: e.target.checked })} />
                        </FormControl>
                        <FormControl display="flex" alignItems="center">
                          <FormLabel mb={0} minW="160px">Skills</FormLabel>
                          <Switch isChecked={Boolean(newBU.has_skills)} onChange={(e) => setNewBU({ ...newBU, has_skills: e.target.checked })} />
                        </FormControl>
                        <FormControl display="flex" alignItems="center">
                          <FormLabel mb={0} minW="160px">Pilot</FormLabel>
                          <Switch isChecked={Boolean(newBU.has_pilot)} onChange={(e) => setNewBU({ ...newBU, has_pilot: e.target.checked })} />
                        </FormControl>
                        <FormControl display="flex" alignItems="center">
                          <FormLabel mb={0} minW="160px">Partners</FormLabel>
                          <Switch isChecked={Boolean(newBU.has_partners)} onChange={(e) => setNewBU({ ...newBU, has_partners: e.target.checked })} />
                        </FormControl>
                      </Stack>
                    </Box>
                  </Stack>
                </ModalBody>
                <ModalFooter>
                  <Button onClick={closeBUCreate} mr={3} variant="ghost">Cancel</Button>
                  <Button colorScheme="teal" onClick={createBU} isLoading={buSaving}>Create</Button>
                </ModalFooter>
              </ModalContent>
            </Modal>

            {/* Edit BU modal */}
            <Modal isOpen={isBUEditOpen} onClose={closeBUEdit} size="lg">
              <ModalOverlay />
              <ModalContent>
                <ModalHeader>Edit Business Unit</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                  <Stack spacing={4}>
                    <FormControl isRequired>
                      <FormLabel>Unit name</FormLabel>
                      <Input value={editBU.unit_name} onChange={(e) => setEditBU({ ...editBU, unit_name: e.target.value })} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Manager's name</FormLabel>
                      <Input value={editBU.manager_name} onChange={(e) => setEditBU({ ...editBU, manager_name: e.target.value })} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Billing manager's name</FormLabel>
                      <Input value={editBU.billing_manager_name} onChange={(e) => setEditBU({ ...editBU, billing_manager_name: e.target.value })} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Billing manager's email</FormLabel>
                      <Input type="email" value={editBU.billing_manager_email} onChange={(e) => setEditBU({ ...editBU, billing_manager_email: e.target.value })} />
                    </FormControl>
                    <Box pt={2}>
                      <FormLabel m={0}>Module access</FormLabel>
                      <Stack spacing={2} mt={2}>
                        <FormControl display="flex" alignItems="center">
                          <FormLabel mb={0} minW="160px">Profile</FormLabel>
                          <Switch isChecked={Boolean(editBU.has_profile)} onChange={(e) => setEditBU({ ...editBU, has_profile: e.target.checked })} />
                        </FormControl>
                        <FormControl display="flex" alignItems="center">
                          <FormLabel mb={0} minW="160px">CV</FormLabel>
                          <Switch isChecked={Boolean(editBU.has_cv)} onChange={(e) => setEditBU({ ...editBU, has_cv: e.target.checked })} />
                        </FormControl>
                        <FormControl display="flex" alignItems="center">
                          <FormLabel mb={0} minW="160px">Skills</FormLabel>
                          <Switch isChecked={Boolean(editBU.has_skills)} onChange={(e) => setEditBU({ ...editBU, has_skills: e.target.checked })} />
                        </FormControl>
                        <FormControl display="flex" alignItems="center">
                          <FormLabel mb={0} minW="160px">Pilot</FormLabel>
                          <Switch isChecked={Boolean(editBU.has_pilot)} onChange={(e) => setEditBU({ ...editBU, has_pilot: e.target.checked })} />
                        </FormControl>
                        <FormControl display="flex" alignItems="center">
                          <FormLabel mb={0} minW="160px">Partners</FormLabel>
                          <Switch isChecked={Boolean(editBU.has_partners)} onChange={(e) => setEditBU({ ...editBU, has_partners: e.target.checked })} />
                        </FormControl>
                      </Stack>
                    </Box>
                  </Stack>
                </ModalBody>
                <ModalFooter>
                  <Button variant="ghost" mr={3} onClick={closeBUEdit}>Cancel</Button>
                  <Button colorScheme="teal" onClick={saveBU} isLoading={buSaving}>Save</Button>
                </ModalFooter>
              </ModalContent>
            </Modal>

            {/* Invite user modal (scoped to selected BU) */}
            <Modal isOpen={isInviteOpen} onClose={closeInvite} size="lg">
              <ModalOverlay />
              <ModalContent>
                <ModalHeader>Invite user</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                  <Stack spacing={4}>
                    <FormControl isRequired>
                      <FormLabel>Email</FormLabel>
                      <Input placeholder="Email" type="email" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel>First name</FormLabel>
                      <Input placeholder="First name" value={invite.first_name} onChange={(e) => setInvite({ ...invite, first_name: e.target.value })} />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel>Last name</FormLabel>
                      <Input placeholder="Last name" value={invite.last_name} onChange={(e) => setInvite({ ...invite, last_name: e.target.value })} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Business Unit</FormLabel>
                      <Input value={(units.find((u) => String(u.id) === String(selectedUnitId))?.unit_code) || (units.find((u) => String(u.id) === String(selectedUnitId))?.unit_name) || String(selectedUnitId) || "-"} isDisabled />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Role</FormLabel>
                      <Select placeholder="Role" value={invite.role} onChange={(e) => setInvite({ ...invite, role: e.target.value })}>
                        {ROLE_OPTIONS.map((r) => (
                          <option key={r} value={r}>{r}</option>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl>
                      <FormLabel>Job title</FormLabel>
                      <Input value={invite.job_title || ""} onChange={(e) => setInvite({ ...invite, job_title: e.target.value })} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Reporting manager</FormLabel>
                      <Input value={invite.reporting_manager || ""} onChange={(e) => setInvite({ ...invite, reporting_manager: e.target.value })} />
                    </FormControl>
                  </Stack>
                </ModalBody>
                <ModalFooter>
                  <Button variant="ghost" mr={3} onClick={closeInvite} isDisabled={inviteSubmitting}>Cancel</Button>
                  <Button colorScheme="teal" isLoading={inviteSubmitting} onClick={() => { if (inviteSubmitting) return; setInviteSubmitting(true); inviteToSelectedBU(); }}>Send invite</Button>
                </ModalFooter>
              </ModalContent>
            </Modal>

            <EditBUUserModal
              isOpen={isEditUserOpen}
              onClose={closeEditUser}
              user={editingUser}
              units={units}
              companyId={companyId}
              selectedUnitId={selectedUnitId}
              membershipsByUserId={membershipsByUserId}
              onSaved={async () => {
                setMembershipsByUserId({});
                const list = await saApi.listCompanyUsers(companyId, selectedUnitId ? { business_unit_id: selectedUnitId } : undefined);
                setBuUsers(Array.isArray(list) ? list : []);
                await loadCompanyUsers();
              }}
            />

            {/* Invite company user modal */}
            <Modal isOpen={isInviteCompanyUserOpen} onClose={closeInviteCompanyUser} size="lg">
              <ModalOverlay />
              <ModalContent>
                <ModalHeader>Invite user to company</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                  <Stack spacing={4}>
                    <FormControl isRequired>
                      <FormLabel>Email</FormLabel>
                      <Input placeholder="Email" type="email" value={companyInvite.email} onChange={(e) => setCompanyInvite({ ...companyInvite, email: e.target.value })} />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel>First name</FormLabel>
                      <Input placeholder="First name" value={companyInvite.first_name} onChange={(e) => setCompanyInvite({ ...companyInvite, first_name: e.target.value })} />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel>Last name</FormLabel>
                      <Input placeholder="Last name" value={companyInvite.last_name} onChange={(e) => setCompanyInvite({ ...companyInvite, last_name: e.target.value })} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Role</FormLabel>
                      <Input value="Company Admin" isDisabled />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Job title</FormLabel>
                      <Input value={companyInvite.job_title || ""} onChange={(e) => setCompanyInvite({ ...companyInvite, job_title: e.target.value })} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Reporting manager</FormLabel>
                      <Input value={companyInvite.reporting_manager || ""} onChange={(e) => setCompanyInvite({ ...companyInvite, reporting_manager: e.target.value })} />
                    </FormControl>
                  </Stack>
                </ModalBody>
                <ModalFooter>
                  <Button variant="ghost" mr={3} onClick={closeInviteCompanyUser} isDisabled={companyInviteSubmitting}>Cancel</Button>
                  <Button colorScheme="blue" isLoading={companyInviteSubmitting} onClick={() => { if (companyInviteSubmitting) return; setCompanyInviteSubmitting(true); inviteCompanyUser(); }}>Send invite</Button>
                </ModalFooter>
              </ModalContent>
            </Modal>

            <EditCompanyUserModal
              isOpen={isEditCompanyUserOpen}
              onClose={closeEditCompanyUser}
              user={editingCompanyUser}
              units={units}
              companyId={companyId}
              onSaved={async () => {
                await loadCompanyUsers();
                if (selectedUnitId) {
                  const list = await saApi.listCompanyUsers(companyId, { business_unit_id: selectedUnitId });
                  setBuUsers(Array.isArray(list) ? list : []);
                }
              }}
            />
          </TabPanel>
          <TabPanel>
            <Flex justify="space-between" align="center" mb={3}>
              <Box />
              <Button size="sm" colorScheme="teal" onClick={openCreate}>Create a new template</Button>
            </Flex>

            {templatesLoading ? (
              <Spinner />
            ) : templatesError ? (
              <Box color="red.500">{templatesError}</Box>
            ) : templates.length === 0 ? (
              <Box bg="white" borderWidth="1px" borderRadius="md" p={8} textAlign="center">
                <Text fontSize="md" color="gray.600" mb={3}>No templates found for this company.</Text>
                <Button colorScheme="teal" onClick={openCreate}>Create a template</Button>
              </Box>
            ) : (
              <Table variant="simple" bg="white" borderWidth="1px" borderRadius="md">
                <Thead>
                  <Tr>
                    <Th>ID</Th>
                    <Th>Business Unit</Th>
                    <Th>Name</Th>
                    <Th>Doc type</Th>
                    <Th>Description</Th>
                    <Th>Path</Th>
                    <Th></Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {templates.map((t) => {
                    const bu = (Array.isArray(businessUnits) ? businessUnits : []).find((b) => String(b.id) === String(t.business_unit_id));
                    const buLabel = bu ? (bu.unit_name || bu.unitName || bu.unit_code || bu.unitCode || bu.id) : "-";
                    return (
                      <Tr key={t.id}>
                        <Td>{t.id}</Td>
                        <Td>{buLabel}</Td>
                        <Td>{t.name}</Td>
                        <Td>{t.doc_type}</Td>
                        <Td>{t.description || ""}</Td>
                        <Td>{t.gcs_path}</Td>
                        <Td><Button size="sm" onClick={() => removeTemplate(t.id)}>Delete</Button></Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            )}

            <Modal isOpen={isCreateOpen} onClose={() => { closeCreate(); resetUploadForm(); }} size="lg">
              <ModalOverlay />
              <ModalContent>
                <ModalHeader>Create a new template</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
                    <FormControl isRequired>
                      <FormLabel m={0}>Name</FormLabel>
                      <Input placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} />
                    </FormControl>
                    <FormControl>
                      <FormLabel m={0}>Description</FormLabel>
                      <Input placeholder="Description" value={description} onChange={(e) => setDescription(e.target.value)} />
                    </FormControl>
                  </SimpleGrid>

                  <FormControl isRequired>
                    <FormLabel m={0}>Business Unit</FormLabel>
                    <Select placeholder="Select a business unit" value={uploadBuId} onChange={(e) => setUploadBuId(e.target.value)}>
                      {(Array.isArray(businessUnits) ? businessUnits : []).map((bu) => (
                        <option key={bu.id} value={bu.id}>
                          {(bu.unit_name || bu.unitName || bu.unit_code || bu.unitCode || bu.id)}
                        </option>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl isRequired mt={4}>
                    <FormLabel m={0}>File</FormLabel>
                    <Box
                      border="2px dashed"
                      borderColor={borderColor}
                      borderRadius="md"
                      px={4}
                      py={6}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      transition="all 0.2s"
                      cursor="pointer"
                      onClick={(e) => {
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.onchange = (ev) => {
                          const target = ev.target;
                          const files = target && target.files ? target.files : null;
                          setFile(files && files[0] ? files[0] : null);
                        };
                        input.click();
                      }}
                      _hover={{ bg: bgHover }}
                    >
                      <HStack spacing={2} align="center">
                        <Icon as={FaCloudUploadAlt} boxSize={5} color="gray.400" />
                        <Text fontSize="sm" color="gray.600" lineHeight="1">Click to select a file</Text>
                      </HStack>
                    </Box>
                    {file && (
                      <Tag size="sm" mt={2} variant="subtle" colorScheme="gray">
                        <TagLabel>{file.name}</TagLabel>
                        <TagCloseButton onClick={() => setFile(null)} />
                      </Tag>
                    )}
                  </FormControl>
                </ModalBody>
                <ModalFooter>
                  <HStack spacing={3}>
                    <Button onClick={() => { closeCreate(); resetUploadForm(); }} isDisabled={createSubmitting}>Cancel</Button>
                    <Button
                      colorScheme="teal"
                      onClick={async () => {
                        if (!file || !name || !uploadBuId || createSubmitting) return;
                        setCreateSubmitting(true);
                        try {
                          await createTemplate();
                          closeCreate();
                        } finally {
                          setCreateSubmitting(false);
                        }
                      }}
                      isDisabled={!file || !name || !uploadBuId || createSubmitting}
                      isLoading={createSubmitting}
                    >
                      Create template
                    </Button>
                  </HStack>
                </ModalFooter>
              </ModalContent>
            </Modal>
          </TabPanel>

          <TabPanel>
            <LicenseHistoryPanel companyId={companyId} />
          </TabPanel>

        </TabPanels>
      </Tabs>

      <ConfirmDialog
        isOpen={isDeleteCompanyOpen}
        onClose={closeDeleteCompany}
        onConfirm={handleDeleteCompany}
        title={t("superAdmin.deleteCompany.title")}
        body={t("superAdmin.deleteCompany.body", { name: company?.company_name || company?.company_code || "" })}
        confirmLabel={t("superAdmin.deleteCompany.confirm")}
        isLoading={deletingCompany}
      />

      <ConfirmDialog
        isOpen={isDeleteBUOpen}
        onClose={closeDeleteBU}
        onConfirm={handleDeleteBU}
        title={t("superAdmin.deleteBusinessUnit.title")}
        body={t("superAdmin.deleteBusinessUnit.body", { name: buDeleteTarget?.unit_name || buDeleteTarget?.unit_code || "" })}
        confirmLabel={t("superAdmin.deleteBusinessUnit.confirm")}
        isLoading={deletingBU}
      />
    </Box>
  );
};

// ---------------------------------------------------------------------------
// License History Panel (lazy-loaded when tab is selected)
// ---------------------------------------------------------------------------

const ACTION_COLORS = {
  ASSIGNED: "green",
  UNASSIGNED: "red",
  LIMIT_UPDATED: "blue",
};

const LicenseHistoryPanel = ({ companyId }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [events, setEvents] = useState([]);
  const [snapshots, setSnapshots] = useState([]);
  const [totalEvents, setTotalEvents] = useState(0);
  const [page, setPage] = useState(1);
  const [actionFilter, setActionFilter] = useState("");
  const pageSize = 20;

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = { page, page_size: pageSize };
      if (actionFilter) params.action = actionFilter;
      const data = await saApi.getCompanyPilotLicenseHistory(companyId, params);
      setEvents(Array.isArray(data?.events) ? data.events : []);
      setSnapshots(Array.isArray(data?.monthly_snapshots) ? data.monthly_snapshots : []);
      setTotalEvents(data?.total_events ?? 0);
    } catch (e) {
      setError("Failed to load license history");
    } finally {
      setLoading(false);
    }
  }, [companyId, page, actionFilter]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.max(1, Math.ceil(totalEvents / pageSize));

  return (
    <Box>
      {/* Monthly snapshot chart */}
      {snapshots.length > 0 && (
        <Box mb={6}>
          <Heading as="h4" size="sm" mb={3}>Active Licenses per Month</Heading>
          <Box bg="white" borderWidth="1px" borderRadius="md" p={4}>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={snapshots} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="active_licenses" name="Active Licenses" fill="#2557A7" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Box>
      )}

      {/* Audit event log */}
      <Flex justify="space-between" align="center" mb={3}>
        <Heading as="h4" size="sm">Audit Events ({totalEvents})</Heading>
        <HStack>
          <Select size="sm" w="180px" value={actionFilter} onChange={(e) => { setActionFilter(e.target.value); setPage(1); }}>
            <option value="">All actions</option>
            <option value="ASSIGNED">Assigned</option>
            <option value="UNASSIGNED">Unassigned</option>
            <option value="LIMIT_UPDATED">Limit Updated</option>
          </Select>
        </HStack>
      </Flex>

      {loading ? (
        <Center py={8}><Spinner /></Center>
      ) : error ? (
        <Box color="red.500">{error}</Box>
      ) : events.length === 0 ? (
        <Text color="gray.500" py={4}>No license audit events found.</Text>
      ) : (
        <>
          <Table variant="simple" size="sm" bg="white" borderWidth="1px" borderRadius="md">
            <Thead>
              <Tr>
                <Th>Date</Th>
                <Th>Action</Th>
                <Th>User</Th>
                <Th>Performed by</Th>
                <Th>Details</Th>
              </Tr>
            </Thead>
            <Tbody>
              {events.map((evt) => (
                <Tr key={evt.id}>
                  <Td whiteSpace="nowrap">{evt.created_at ? new Date(evt.created_at).toLocaleString() : "—"}</Td>
                  <Td>
                    <Badge colorScheme={ACTION_COLORS[evt.action] || "gray"}>{evt.action}</Badge>
                  </Td>
                  <Td>
                    {evt.user_name || evt.user_email || "—"}
                    {evt.user_name && evt.user_email && (
                      <Text as="span" fontSize="xs" color="gray.500" ml={1}>({evt.user_email})</Text>
                    )}
                  </Td>
                  <Td>{evt.actor_email || "—"}</Td>
                  <Td>
                    {evt.action === "LIMIT_UPDATED" && evt.old_limit != null && evt.new_limit != null
                      ? `${evt.old_limit} → ${evt.new_limit}`
                      : evt.reason || "—"}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>

          {/* Pagination */}
          {totalPages > 1 && (
            <HStack justify="center" mt={4} spacing={2}>
              <Button size="sm" isDisabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Button>
              <Text fontSize="sm">Page {page} of {totalPages}</Text>
              <Button size="sm" isDisabled={page >= totalPages} onClick={() => setPage(page + 1)}>Next</Button>
            </HStack>
          )}
        </>
      )}
    </Box>
  );
};

export default CompanyDetail;


